import * as yup from 'yup'
import { phoneNewRegExp } from '../../components/waiver-steps-2/helpers'
import { USCountryVariations, ValidateLicense, validateState } from '../driversLicense'
import { validateDateFormat, maxAgeCheck, minAgeCheck } from '../date-operations'

export const contactInfoValidation = (isGuest, t) => {
  const birthDateValidation = yup
    .mixed()
    .required(t('required'))
    .test({
      name: 'format',
      message: t('validFormat'),
      test: (val) => validateDateFormat(val)
    })
    .test({
      name: 'Under 18',
      message: t('riderMustBeOlderThan18'),
      test: (val) => (validateDateFormat(val) ? minAgeCheck(val) : false)
    })
    .test({
      name: 'Invalid Date',
      message: t('invalidDate'),
      test: (val) => (validateDateFormat(val) ? maxAgeCheck(val) : false)
    })

  return yup.object().shape({
    birthDate: isGuest ? birthDateValidation : undefined,
    firstName: yup.string().trim().required(t('required')).nullable(),
    lastName: yup.string().trim().required(t('required')).nullable(),
    email: yup.string().trim().required(t('required')).email(t('invalidEmail')),
    phone: yup
      .string()
      .trim()
      .required(t('required'))
      .when('country', {
        is: (val) => {
          return USCountryVariations.USA.includes(val)
        },
        then: yup.string().matches(phoneNewRegExp, t('invalidPhoneNumber'))
      }),
    addressLine1: yup.string().trim().required(t('required')),
    addressLine2: yup.string(),
    city: yup.string().trim().required(t('required')),
    postalCode: yup
      .string()
      .required(t('required'))
      .when('country', {
        is: (val) => USCountryVariations.USA.includes(val),
        then: yup.string().min(5, t('invalid')).max(10, t('invalid'))
      }),
    state: yup
      .string()
      .uppercase()
      .required(t('required'))
      .when('country', {
        is: (val) => {
          return USCountryVariations.USA.includes(val)
        },
        then: yup
          .string()
          .max(2, t('useStateAbbreviation'))
          .test('ValidState', t('invalidState'), (value) => validateState(value))
      }),
    country: yup.string().required(t('required')).notOneOf(['none'])
  })
}

export const LicenseInfoValidation = (isGuest, productLine, payload, t) => {
  const birthDateValidation = yup
    .mixed()
    .required(t('required'))
    .test({
      name: 'format',
      message: t('validFormat'),
      test: (val) => validateDateFormat(val)
    })
    .test({
      name: 'Under 18',
      message: t('riderMustBeOlderThan18'),
      test: (val) => (validateDateFormat(val) ? minAgeCheck(val) : false)
    })
    .test({
      name: 'Invalid Date',
      message: t('invalidDate'),
      test: (val) => (validateDateFormat(val) ? maxAgeCheck(val) : false)
    })

  return yup.object().when('driverLicenseInfoRequired', {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    is: (val) => true,
    then: yup.object().shape({
      birthDate: !isGuest ? birthDateValidation : undefined,
      driversLicenseState: yup
        .string()
        .uppercase()
        .required(t('required'))
        .when('driversLicenseCountry', {
          is: (val) => USCountryVariations.USA.includes(val),
          then: yup
            .string()
            .max(2, t('useStateAbbreviation'))
            .test('ValidState', t('invalidState'), (value) => validateState(value))
        }),
      driversLicenseCountry: yup.string().required(t('required')).notOneOf(['none']),
      driversLicense: yup.string().required(t('required')),
      driverHasMotorcycleLicense: yup.boolean().test('productLineTruthy', t('requiredToRide'), (val) => {
        if (productLine === 'IND') {
          return !!val
        }
        return true
      })
    })
  })
}

export const AdditionalParticipantsValidation = (isGuest, productLine, payload, participants, isEhsRequired, t) => {
  return yup.object().shape({
    participants: yup.array().of(
      yup.object().shape(
        {
          firstName: yup.string().when('lastName', {
            is: '',
            then: yup.string(),
            otherwise: yup.string().required(t('required'))
          }),
          lastName: yup.string().when('firstName', {
            is: '',
            then: yup.string(),
            otherwise: yup.string().required(t('required'))
          })
        },
        [['firstName', 'lastName']]
      )
    ),
    minorParticipantsCheckbox: yup.boolean().when('isEhsRequired', {
      is: true,
      then: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val }),
      otherwise: yup.boolean()
    }),
    minorParticipantsAccompaniedCheckbox: yup.boolean().when('isGuest', {
      is: true,
      then: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val }),
      otherwise: yup.boolean()
    }),
    minorParticipantsNotDriveCheckbox: yup.boolean().when('isGuest', {
      is: true,
      then: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val }),
      otherwise: yup.boolean()
    }),
    minorParticipantsPassengersCheckbox: yup.boolean().when('isGuest', {
      is: true,
      then: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val }),
      otherwise: yup.boolean()
    }),
    minorParticipantsBySignCheckbox: yup.boolean().when('isGuest', {
      is: true,
      then: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val }),
      otherwise: yup.boolean()
    })
  })
}

export const ReviewAgreementValidation = (
  optInCheckCheck,
  t,
  { overviewQ1, overviewQ2, overviewQ3, overviewQ4, overviewQ5, overviewQ6 }
) => {
  return yup.object().shape({
    waiverAgreementCheck: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val }),
    optInCheck: yup.boolean().when('optInCheckCheck', {
      is: true,
      then: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val }),
      otherwise: yup.boolean()
    }),
    overviewQ1: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val || !overviewQ1 }),
    overviewQ2: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val || !overviewQ2 }),
    overviewQ3: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val || !overviewQ3 }),
    overviewQ4: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val || !overviewQ4 }),
    overviewQ5: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val || !overviewQ5 }),
    overviewQ6: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val || !overviewQ6 })
  })
}

export const SignatureValidation = (safetyVideoRequired, safetyVideoCheck, t) => {
  return yup.object().shape({
    safetyVideoRequired: yup.boolean().when('isSafetyVideoRequired', {
      is: true,
      then: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val }),
      otherwise: yup.boolean()
    }),
    signatureIsNotEmpty: yup.boolean().when('isSignatureRequired', {
      is: true,
      then: yup.boolean().test({ name: 'truthy', message: t('required'), test: (val) => !!val }),
      otherwise: yup.boolean()
    }),
    isSafetyVideoRequired: yup.boolean(),
    isSignatureRequired: yup.boolean()
  })
}
